import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Grid,
	Typography,
	Button,
	Pagination,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	Box,
	IconButton,
	TextField,
	CircularProgress,
} from "@mui/material";

import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

import { SubHeader } from "../../utils/componentes-utiles/SubHeader";
import { useNavigate, useLocation } from "react-router-dom";
import { getUsuariosMovil, sincronizar } from "../../services/apis";
import { AvatarPersona } from "./AvatarPersona";

export const Personas = () => {
	const token =
		localStorage.getItem("accessToken") ||
		sessionStorage.getItem("accessToken") ||
		"";

	const [openRow, setOpenRow] = useState(null);
	const [personas, setPersonas] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState("");
	const [busqueda, setBusqueda] = useState("");
	const [filtroFoto, setFiltroFoto] = useState("todas");
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(25);
	const [totalPages, setTotalPages] = useState(1);
	const [syncing, setSyncing] = useState(false);
	const [syncMsg, setSyncMsg] = useState("");
	const [openSyncDialog, setOpenSyncDialog] = useState(false);
	const [syncMode, setSyncMode] = useState("hoy");
	const [fichajePreview, setFichajePreview] = useState(null);
	const [fichajePreviewSrc, setFichajePreviewSrc] = useState("");
	const [fichajePreviewLoading, setFichajePreviewLoading] = useState(false);
	const [fichajePreviewError, setFichajePreviewError] = useState("");

	const navigate = useNavigate();
	const location = useLocation();
	const isActive = (path) => location.pathname === path;

	const debouncedSearch = useDebouncedValue(busqueda, 350);
	const prevQueryRef = useRef({ debouncedSearch, filtroFoto });

	const hoyYMD = useMemo(() => {
		const d = new Date();
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
			d.getDate()
		).padStart(2, "0")}`;
	}, []);

	const [desde, setDesde] = useState(hoyYMD);
	const [hasta, setHasta] = useState(hoyYMD);

	const cargarPersonas = async () => {
		setLoaded(false);
		setError("");

		try {
			const response = await getUsuariosMovil({
				page,
				limit,
				search: debouncedSearch,
				foto: filtroFoto,
				historial: true,
				historialLimit: 5,
			});

			if (response?.success === true) {
				const usuarios = response.data?.usuarios ?? [];
				const pag = response.data?.pagination ?? {};

				setPersonas(usuarios);
				setTotalPages(pag.total_pages ?? 1);
			} else {
				setPersonas([]);
				setTotalPages(1);
				setError(response?.message || "No se pudieron cargar los usuarios");
			}
		} catch (e) {
			console.error(e);
			setPersonas([]);
			setTotalPages(1);
			setError("Error de red al cargar usuarios");
		} finally {
			setLoaded(true);
		}
	};

	const handleAvatarUploaded = (updatedPersona) => {
		setPersonas((currentPersonas) =>
			currentPersonas.map((persona) =>
				persona.id === updatedPersona.id
					? { ...persona, imagen_url: updatedPersona.imagen_url }
					: persona
			)
		);
	};

	useEffect(() => {
		setPage(1);
		setOpenRow(null);
	}, [debouncedSearch, filtroFoto]);

	useEffect(() => {
		const prev = prevQueryRef.current;
		const searchChanged = prev.debouncedSearch !== debouncedSearch;
		const fotoChanged = prev.filtroFoto !== filtroFoto;

		prevQueryRef.current = { debouncedSearch, filtroFoto };

		if ((searchChanged || fotoChanged) && page !== 1) return;

		cargarPersonas();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, limit, debouncedSearch, filtroFoto]);

	const getFichajeImageEndpoint = (histItem) => {
		if (
			typeof histItem?.url_imagen_endpoint === "string" &&
			histItem.url_imagen_endpoint.trim()
		) {
			return histItem.url_imagen_endpoint.trim();
		}

		const idEmpleo = Number(histItem?.id_empleo);
		const urlImagen =
			typeof histItem?.url_imagen === "string" ? histItem.url_imagen.trim() : "";

		if (!idEmpleo || !urlImagen) return "";

		return `https://recon.jcinformatics.com/imagefichaje.php?id_empleo=${idEmpleo}`;
	};

	const abrirImagenFichaje = (persona, histItem) => {
		if (!persona || !histItem) return;

		const endpoint = getFichajeImageEndpoint(histItem);
		if (!endpoint) return;

		setFichajePreview({
			personaNombre:
				persona?.nombre_completo ||
				`${persona?.apellidos ?? ""} ${persona?.nombre ?? ""}`.trim() ||
				"Persona",
			fecha: histItem?.fecha_cuadrilla || "",
			endpoint,
		});
	};

	const cerrarImagenFichaje = () => {
		setFichajePreview(null);
	};

	useEffect(() => {
		let active = true;
		let objectUrl = null;

		const cargarImagenFichaje = async () => {
			if (!fichajePreview?.endpoint || !token) {
				setFichajePreviewSrc("");
				setFichajePreviewLoading(false);
				setFichajePreviewError("");
				return;
			}

			setFichajePreviewLoading(true);
			setFichajePreviewError("");
			setFichajePreviewSrc("");

			try {
				const separator = fichajePreview.endpoint.includes("?") ? "&" : "?";
				const endpoint = `${fichajePreview.endpoint}${separator}t=${Date.now()}`;
				const res = await fetch(endpoint, {
					headers: {
						Authorization: `Bearer ${token}`,
						"Cache-Control": "no-cache",
						Pragma: "no-cache",
					},
				});

				if (!res.ok) throw new Error("No se pudo cargar la imagen del fichaje");

				const blob = await res.blob();
				objectUrl = URL.createObjectURL(blob);

				if (active) setFichajePreviewSrc(objectUrl);
			} catch (err) {
				console.error("Error cargando imagen del fichaje:", err);
				if (active) {
					setFichajePreviewSrc("");
					setFichajePreviewError("No se pudo cargar la imagen del fichaje");
				}
			} finally {
				if (active) setFichajePreviewLoading(false);
			}
		};

		cargarImagenFichaje();

		return () => {
			active = false;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [fichajePreview?.endpoint, token]);

	const handleSincronizar = async (payload) => {
		setSyncing(true);
		setSyncMsg("");

		try {
			const res = await sincronizar(payload);

			if (res?.success) {
				if (res.modo === "rango") {
					const okDays = Object.values(res.dias || {}).filter(
						(d) => d.ws_ok && !d.error
					).length;
					setSyncMsg(`Sincronizacion completada (${okDays} dias procesados)`);
				} else {
					setSyncMsg(
						`Sincronizacion completada (${res?.dias?.[hoyYMD]?.ptegrupo?.ptegrupo_creados_o_actualizados ?? 0} partes)`
					);
				}

				await cargarPersonas();
			} else {
				setSyncMsg(res?.message || "Error al sincronizar");
			}
		} catch (e) {
			console.error(e);
			setSyncMsg("Error de red al sincronizar");
		} finally {
			setSyncing(false);
		}
	};

	const submitSync = async () => {
		if (syncMode === "rango") {
			if (!desde || !hasta) {
				setSyncMsg("Selecciona desde y hasta");
				return;
			}

			if (desde > hasta) {
				setSyncMsg("'Desde' no puede ser mayor que 'Hasta'");
				return;
			}

			setOpenSyncDialog(false);
			await handleSincronizar({ desde, hasta });
			return;
		}

		setOpenSyncDialog(false);
		await handleSincronizar({ fecha: hoyYMD });
	};

	const abrirDetallePersona = (persona) => {
		if (!persona?.id) return;

		navigate(`/personas/${persona.id}`, {
			state: {
				persona,
				from: "/personas",
			},
		});
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }}>
			<Grid item size={{ xs: 12 }}>
				<SubHeader titulo="Personas" />
			</Grid>

			<Grid container spacing={2} sx={{ px: 2, pb: 2 }} alignItems="center">
				<Button variant="text" onClick={() => setOpenSyncDialog(true)} disabled={syncing}>
					<CloudSyncIcon
						sx={{
							opacity: 0.7,
							animation: syncing ? "spin 1s linear infinite" : "none",
						}}
					/>
					&nbsp;{syncing ? "Sincronizando..." : "Sincronizar"}
				</Button>

				<Button
					variant="text"
					onClick={() => navigate("/carpetas")}
					sx={{
						fontWeight: isActive("/carpetas") ? 600 : 400,
						borderBottom: isActive("/carpetas")
							? "2px solid"
							: "2px solid transparent",
						borderRadius: 0,
					}}
				>
					Carpetas
				</Button>

				<Button
					variant="text"
					onClick={() => navigate("/documentacion")}
					sx={{
						fontWeight: isActive("/documentacion") ? 600 : 400,
						borderBottom: isActive("/documentacion")
							? "2px solid"
							: "2px solid transparent",
						borderRadius: 0,
					}}
				>
					Documentos
				</Button>
			</Grid>

			<Grid
				container
				justifyContent="space-between"
				alignItems="center"
				sx={{ px: 3, pb: 2 }}
			>
				<Grid container spacing={2} size={{ xs: "auto" }}>
					<Button size="small" variant="outlined">Documento</Button>
					<Button size="small" variant="outlined">Estado</Button>
					<Button size="small" variant="outlined">Tipo</Button>
				</Grid>

				<Grid container spacing={2} size={{ xs: "auto" }} alignItems="center">
					<TextField
						size="small"
						placeholder="Buscar por nombre..."
						value={busqueda}
						onChange={(e) => setBusqueda(e.target.value)}
					/>
					<FormControl size="small" sx={{ minWidth: 140 }}>
						<InputLabel id="filtro-foto-label">Foto</InputLabel>
						<Select
							labelId="filtro-foto-label"
							label="Foto"
							value={filtroFoto}
							onChange={(e) => setFiltroFoto(e.target.value)}
						>
							<MenuItem value="todas">Todas</MenuItem>
							<MenuItem value="con">Con foto</MenuItem>
							<MenuItem value="sin">Sin foto</MenuItem>
						</Select>
					</FormControl>
					<Button variant="outlined" startIcon={<FilterAltIcon />}>Filtros</Button>
					<Button variant="outlined" startIcon={<FileDownloadIcon />}>Exportar</Button>
					<Button variant="outlined" startIcon={<ViewColumnIcon />}>Columnas</Button>
				</Grid>
			</Grid>

			{syncMsg && (
				<Grid sx={{ px: 3, py: 1 }}>
					<Typography
						variant="body2"
						color={syncMsg.toLowerCase().includes("error") ? "error" : "success.main"}
					>
						{syncMsg}
					</Typography>
				</Grid>
			)}

			{!loaded && (
				<Grid sx={{ px: 3, py: 2 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<CircularProgress size={18} />
						<Typography variant="body2">Cargando usuarios...</Typography>
					</Box>
				</Grid>
			)}

			{loaded && error && (
				<Grid sx={{ px: 3, py: 2 }}>
					<Typography variant="body2" color="error">
						{error}
					</Typography>
				</Grid>
			)}

			<Grid sx={{ width: "100%", overflowX: { xs: "auto", md: "visible" } }}>
				<Grid container direction="column" sx={{ minWidth: 1100 }}>
					<Grid
						container
						alignItems="center"
						sx={{
							px: 3,
							py: 1,
							bgcolor: "grey.100",
							borderBottom: "1px solid",
							borderColor: "divider",
						}}
					>
						<Grid item size={{ xs: 0.4 }} />
						<Grid item size={{ xs: 1.1 }}>Documentos</Grid>
						<Grid item size={{ xs: 2.4 }}>Nombre</Grid>
						<Grid item size={{ xs: 1.1 }}>NIF</Grid>
						<Grid item size={{ xs: 1.6 }}>Departamento</Grid>
						<Grid item size={{ xs: 1.1 }}>Rol</Grid>
						<Grid item size={{ xs: 1.8 }}>Email</Grid>
					</Grid>

					{loaded && !error && personas.map((u) => {
						const historial = Array.isArray(u.historial_fichajes) ? u.historial_fichajes : [];

						return (
							<React.Fragment key={u.id}>
							<Grid
								container
								alignItems="center"
								sx={{
									px: 3,
									py: 1,
									borderBottom: "1px solid",
									borderColor: "divider",
									"&:hover": { bgcolor: "action.hover" },
								}}
							>
								<Grid item size={{ xs: 0.3 }}>
									<IconButton
										size="small"
										onClick={() => setOpenRow(openRow === u.id ? null : u.id)}
									>
										{openRow === u.id ? <ExpandMoreIcon /> : <ChevronRightIcon />}
									</IconButton>
								</Grid>

								<Grid item size={{ xs: 1.1 }}>
									<Box sx={{ display: "flex", gap: 0.4 }}>
										<InsertDriveFileIcon fontSize="small" color="success" />
										<InsertDriveFileIcon fontSize="small" color="warning" />
										<InsertDriveFileIcon fontSize="small" color="error" />
										<InsertDriveFileIcon fontSize="small" color="disabled" />
									</Box>
								</Grid>

								<Grid
									item
									size={{ xs: 2.4 }}
									sx={{ display: "flex", gap: 1, alignItems: "center" }}
								>
									<AvatarPersona
										persona={u}
										token={token}
										size={26}
										allowUpload
										onAvatarUploaded={handleAvatarUploaded}
									/>
									<Typography
										variant="body2"
										noWrap
										onClick={() => abrirDetallePersona(u)}
										sx={{
											cursor: "pointer",
											"&:hover": {
												textDecoration: "underline",
												color: "primary.main",
											},
										}}
									>
										{u.nombre_completo ||
											`${u.apellidos ?? ""} ${u.nombre ?? ""}`.trim() ||
											"-"}
									</Typography>
								</Grid>

								<Grid item size={{ xs: 1.1 }}>
									<Typography variant="body2" noWrap>{u.nif || "-"}</Typography>
								</Grid>

								<Grid item size={{ xs: 1.6 }}>
									<Typography variant="body2" noWrap>
										{u.nombre_departamento || "-"}
									</Typography>
								</Grid>

								<Grid item size={{ xs: 1.1 }}>
									<Chip size="small" label={u.nombre_rol || "-"} />
								</Grid>

								<Grid item size={{ xs: 1.8 }}>
									<Typography variant="body2" noWrap>{u.mail || "-"}</Typography>
								</Grid>
							</Grid>

							<Collapse in={openRow === u.id}>
								<Grid
									container
									sx={{
										bgcolor: "grey.50",
										borderBottom: "1px solid",
										borderColor: "divider",
									}}
								>
									<Grid container direction="column" sx={{ px: 3, py: 2, overflowX: "auto" }}>
										<Typography variant="subtitle2" sx={{ mb: 1 }}>
											Historial de fichajes
										</Typography>

										{historial.length === 0 ? (
											<Typography variant="body2" sx={{ opacity: 0.7 }}>
												Sin fichajes recientes.
											</Typography>
										) : (
											<Grid container spacing={1}>
												{historial.map((h) => (
													<Grid
														key={`${h.id_cuadrilla}-${h.fecha_cuadrilla}`}
														item
														size={{ xs: 12 }}
													>
														<Box
																						sx={{
																							display: "flex",
																							alignItems: "center",
																							gap: 1,
																							flexWrap: "wrap",
																						}}
																					>
																						<Typography
																							variant="body2"
																							sx={{ minWidth: 140 }}
																						>
																							{formatDateTime(h.fecha_cuadrilla)}
																						</Typography>
																						<Typography
																							variant="body2"
																							sx={{ opacity: 0.8 }}
																						>
																							{h.nombre_cliente || "-"}
																						</Typography>
																						<Chip
																							size="small"
																							label={h.estado_label || "-"}
																							color={getEstadoColor(h.estado)}
																						/>
																						<Chip
																							size="small"
																							variant="outlined"
																							icon={<PhotoCameraIcon />}
																							label="Foto"
																							disabled={!getFichajeImageEndpoint(h)}
																							clickable={!!getFichajeImageEndpoint(h)}
																							onClick={
																								getFichajeImageEndpoint(h)
																									? () => abrirImagenFichaje(u, h)
																									: undefined
																							}
																							title={
																								getFichajeImageEndpoint(h)
																									? "Ver imagen asociada al fichaje"
																									: "Este fichaje no tiene imagen asociada"
																							}
																						/>
																					</Box>
																				</Grid>
																			))}
																		</Grid>
																	)}
									</Grid>
								</Grid>
							</Collapse>
						</React.Fragment>
						);
					})}

					{loaded && !error && personas.length === 0 && (
						<Grid sx={{ px: 3, py: 2 }}>
							<Typography variant="body2">No hay usuarios para mostrar.</Typography>
						</Grid>
					)}
				</Grid>
			</Grid>

			<Grid
				container
				justifyContent="space-between"
				alignItems="center"
				sx={{ px: 3, py: 2 }}
			>
				<FormControl variant="standard" sx={{ minWidth: 120 }}>
					<InputLabel>Filas por pagina</InputLabel>
					<Select
						value={limit}
						onChange={(e) => {
							setLimit(Number(e.target.value));
							setPage(1);
						}}
					>
						<MenuItem value={25}>25</MenuItem>
						<MenuItem value={50}>50</MenuItem>
					</Select>
				</FormControl>

				<Pagination
					count={totalPages}
					page={page}
					onChange={(_, p) => {
						setPage(p);
						setOpenRow(null);
					}}
					color="primary"
				/>
			</Grid>

			<Dialog
				open={openSyncDialog}
				onClose={() => setOpenSyncDialog(false)}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle>Sincronizar trabajadores</DialogTitle>
				<DialogContent>
					<RadioGroup value={syncMode} onChange={(e) => setSyncMode(e.target.value)}>
						<FormControlLabel
							value="hoy"
							control={<Radio />}
							label={`Solo hoy (${hoyYMD})`}
						/>
						<FormControlLabel
							value="rango"
							control={<Radio />}
							label="Rango de fechas"
						/>
					</RadioGroup>

					{syncMode === "rango" && (
						<Grid container spacing={2} sx={{ mt: 1 }}>
							<Grid item xs={6}>
								<TextField
									label="Desde"
									type="date"
									size="small"
									fullWidth
									value={desde}
									onChange={(e) => setDesde(e.target.value)}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									label="Hasta"
									type="date"
									size="small"
									fullWidth
									value={hasta}
									onChange={(e) => setHasta(e.target.value)}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>
						</Grid>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenSyncDialog(false)} disabled={syncing}>
						Cancelar
					</Button>
					<Button variant="contained" onClick={submitSync} disabled={syncing}>
						Sincronizar
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={Boolean(fichajePreview)}
				onClose={cerrarImagenFichaje}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>Foto del fichaje</DialogTitle>
				<DialogContent sx={{ display: "flex", justifyContent: "center", p: 2 }}>
					{fichajePreview && (
						<Grid container direction="column" spacing={2}>
							<Grid item size={{ xs: 12 }}>
								<Typography variant="subtitle1">
									{fichajePreview.personaNombre} · {formatDateTime(fichajePreview.fecha)}
								</Typography>
							</Grid>

							<Grid item size={{ xs: 12 }}>
								{fichajePreviewLoading ? (
									<Box
										sx={{
											minHeight: "35vh",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<CircularProgress />
									</Box>
								) : fichajePreviewError ? (
									<Alert severity="error">{fichajePreviewError}</Alert>
								) : (
									<Box
										component="img"
										src={fichajePreviewSrc}
										alt={`Fichaje de ${fichajePreview.personaNombre}`}
										sx={{
											maxWidth: "100%",
											maxHeight: "70vh",
											objectFit: "contain",
											borderRadius: 1,
										}}
									/>
								)}
							</Grid>
						</Grid>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={cerrarImagenFichaje}>Cerrar</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
};

function formatDateTime(value) {
	if (!value) return "-";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);

	return date.toLocaleString("es-ES", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function getEstadoColor(estado) {
	if (estado === "validado") return "success";
	if (estado === "no_reconocido") return "error";
	return "warning";
}

function useDebouncedValue(value, delay = 300) {
	const [debounced, setDebounced] = useState(value);

	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(t);
	}, [value, delay]);

	return debounced;
}
